import React, { useEffect } from 'react';
import {
    LandingPage,
    Home,
    Settings,
    NotFound,
    Logout,
    NotAuthorised,
    Cookies,
    TandC,
    Privacy,
    Contact,
    Accessibility,
    LoginCallback,
} from './pages';

import AdminTabs from './components/layout/AdminTabs';
import ResponsiveOverlay from './components/shared/responsiveOverlay/ResponsiveOverlay';
import ProtectedRedirect from './components/auth/ProtectedRedirect';
import AdminRedirect from './components/auth/admin/AdminRedirect';
import { AppRoutes } from './Constants';
import Layout from './components/layout/Layout';
import UserInfoWrapper from './helpers/UserInfoWrapper';
import { useApplyDarkMode } from './helpers/DarkModeDetector';
import { ApplicationPolicies, Management, Tenant } from './pages/admin';
import SuperAdminRedirect from './components/auth/superAdmin/SuperAdminRedirect';
import { BusinessMetrics } from './pages/metrics';
import i18nSetup from './locale/i18nSetup';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import MyProcesses from './pages/engagements/YourEngagements';
import CreateProcess from './pages/processes/CreateProcess';
import BasicLayout from './components/layout/BasicLayout';
import ProcessPage from './pages/process/Process';
import ProcessEdit from './pages/process/UploadedDocumentsEdit/UploadedDocumentsEdit';
import ProcessingEdit from './pages/process/ProcessingDocumentsEdit/ProcessingDocumentsEdit';
import OnlineFormEdit from './pages/process/OnlineFormEdit/OnlineFormEdit';
import ExtractionSettings from './pages/admin/extraction/ExtractionSettings';
import ExtractionDocTypeSettings from './pages/admin/extraction/DocTypeSettings';
import ExtractionValidationSettings from './pages/admin/extraction/ValidationSettings';
import FillPDFSettings from './pages/admin/fillPDF/FillPDFSettings';
import FillPDFDocTypeSettings from './pages/admin/fillPDF/DocTypeSettings';
import FillPDFValidationSettings from './pages/admin/fillPDF/ValidationSettings';
import FillOnlineSettings from './pages/admin/fillOnline/FillOnlineSettings';
import FillOnlineDocTypeSettings from './pages/admin/fillOnline/DocTypeSettings';
import FillOnlineValidationSettings from './pages/admin/fillOnline/ValidationSettings';
import SettingsLayout from './components/layout/SettingsLayout';

const App = () => {
    i18nSetup();

    useApplyDarkMode();

    const colourTheme: 'orange' | 'pink' | 'red' | 'black' = 'black';

    useEffect(() => {
        document.body.setAttribute('data-theme', colourTheme);
        return () => document.body.removeAttribute('data-theme');
    }, [colourTheme]);

    const router = createBrowserRouter([
        {
            path: AppRoutes.LandingPage,
            element: <LandingPage />,
            index: true,
        },
        {
            // Routes with basic layout (no tabs)
            element: <Layout />,
            children: [
                {
                    element: (
                        <ProtectedRedirect>
                            <UserInfoWrapper />
                        </ProtectedRedirect>
                    ),
                    children: [
                        {
                            path: AppRoutes.HomePage,
                            element: <Home />,
                        },
                        {
                            path: AppRoutes.FolderRoot,
                            element: <MyProcesses />,
                        },
                        {
                            path: AppRoutes.Folder,
                            element: <MyProcesses />,
                        },
                        {
                            path: AppRoutes.CreateProcess,
                            element: <CreateProcess />,
                        },
                        {
                            path: AppRoutes.Process,
                            element: <ProcessPage />,
                            children: [
                                {
                                    path: 'uploaded',
                                    element: <ProcessPage />,
                                },
                                {
                                    path: 'document',
                                    element: <ProcessPage />,
                                },
                            ],
                        },
                        {
                            path: AppRoutes.UploadDocumentEdit,
                            element: <ProcessEdit />,
                        },
                        {
                            path: AppRoutes.ProcessDocumentEdit,
                            element: <ProcessingEdit />,
                        },
                        {
                            path: AppRoutes.OnlineFormEdit,
                            element: <OnlineFormEdit />,
                        },
                        {
                            path: AppRoutes.FillPDFEdit,
                            element: <OnlineFormEdit />,
                        },
                    ],
                },
            ],
        },
        {
            // Routes with full layout (including tabs)
            element: <BasicLayout />,
            children: [
                {
                    element: (
                        <ProtectedRedirect>
                            <UserInfoWrapper />
                        </ProtectedRedirect>
                    ),
                    children: [
                        {
                            path: AppRoutes.HomePage,
                            element: <Home />,
                        },
                        {
                            path: AppRoutes.SettingsPage,
                            element: <Settings />,
                        },
                        {
                            // Routes accessible to admins and super admins
                            element: <AdminRedirect />,
                            children: [
                                {
                                    element: <AdminTabs />,
                                    children: [
                                        {
                                            path: AppRoutes.Management,
                                            element: <Management />,
                                        },

                                        {
                                            element: <SuperAdminRedirect />,
                                            children: [
                                                {
                                                    path: AppRoutes.ApplicationPolicies,
                                                    element: (
                                                        <ApplicationPolicies />
                                                    ),
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            element: <SuperAdminRedirect />,
                            children: [
                                {
                                    path: AppRoutes.BusinessMetrics,
                                    element: <BusinessMetrics />,
                                },

                                {
                                    path: `${AppRoutes.Tenant}/:tenantId`,
                                    element: <Tenant />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: AppRoutes.CookiesPage,
                    element: <Cookies />,
                },

                {
                    path: AppRoutes.TandC,
                    element: <TandC />,
                },
                {
                    path: AppRoutes.PrivacyPage,
                    element: <Privacy />,
                },
                {
                    path: AppRoutes.ContactPage,
                    element: <Contact />,
                },
                {
                    path: AppRoutes.AccessibilityStatement,
                    element: <Accessibility />,
                },
                {
                    path: AppRoutes.LogoutPage,
                    element: <Logout />,
                },
                {
                    path: '/login/callback',
                    element: <LoginCallback />,
                },
                {
                    path: '/403',
                    element: <NotAuthorised />,
                },
                {
                    path: '*',
                    element: <NotFound />,
                },
            ],
        },
        {
            // Routes for admin settings
            element: <SettingsLayout />,
            children: [
                {
                    element: (
                        <ProtectedRedirect>
                            <UserInfoWrapper />
                        </ProtectedRedirect>
                    ),
                    children: [
                        {
                            element: <AdminRedirect />,
                            children: [
                                {
                                    path: AppRoutes.ExtractionSettings,
                                    element: <ExtractionSettings />,
                                },
                                {
                                    path: AppRoutes.ExtractionDocTypeSettings,
                                    element: <ExtractionDocTypeSettings />,
                                },
                                {
                                    path: AppRoutes.ExtractionValidationSettings,
                                    element: <ExtractionValidationSettings />,
                                },
                                {
                                    path: AppRoutes.FillPDFSettings,
                                    element: <FillPDFSettings />,
                                },
                                {
                                    path: AppRoutes.FillPDFDocTypeSettings,
                                    element: <FillPDFDocTypeSettings />,
                                },
                                {
                                    path: AppRoutes.FillPDFValidationSettings,
                                    element: <FillPDFValidationSettings />,
                                },
                                {
                                    path: AppRoutes.FillOnlineSettings,
                                    element: <FillOnlineSettings />,
                                },
                                {
                                    path: AppRoutes.FillOnlineDocTypeSettings,
                                    element: <FillOnlineDocTypeSettings />,
                                },
                                {
                                    path: AppRoutes.FillOnlineValidationSettings,
                                    element: <FillOnlineValidationSettings />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ]);

    return (
        <>
            <ResponsiveOverlay />
            <div className="d-flex flex-column h-100 min-vh-100">
                <RouterProvider router={router} />
            </div>
        </>
    );
};

export default App;
